import { Injectable, inject } from '@angular/core';

import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';

import { Colors2 } from '@23degrees/shared-code/models';
import chroma from 'chroma-js';
import { ascending, quantile } from 'd3-array';
import { scaleLinear, scaleQuantile, scaleThreshold } from 'd3-scale';
import sortBy from 'lodash/sortBy';

import { unique } from '@23degrees/shared-code/utilities';

const normalize = (num, min, max) => (num - min) / (max - min);

@Injectable({
  providedIn: 'root',
})
export class ColorManager extends BaseClass {
  private messageBus = inject(MessageBus);

  public getColors = (colorObj: Colors2, data?: any[]) => {
    if (colorObj.type === 'categorical') {
      const { values, fallback } = colorObj.categorical;
      const colors = values.map((obj) => obj.color);
      colors.push(fallback);
      return colors;
    }

    if (colorObj.type === 'gradient') {
      const { values } = colorObj.gradient;
      return values.map((obj) => obj.color);
    }

    if (colorObj.type === 'range') {
      return colorObj.range.colors;
    }

    if (colorObj.type === 'solid') {
      return [colorObj.solid];
    }

    const { colorSets } = colorObj.continuous;
    if (Array.isArray(colorSets) && Array.isArray(data)) {
      const colorSet = colorSets.find(
        (colorSet) => colorSet.enabled && colorSet.filter.every((filter) => data.every((row) => row[filter.fieldName] === filter.value)),
      );

      if (colorSet) {
        return colorSet.colors;
      }
    }

    return colorObj.continuous.colors;
  };

  public getStyleColorsByCount(colorObj: Colors2, count) {
    const colors = this.getColors(colorObj);
    return this.getColorsByCount(colors, count);
  }

  public getValues(colorsObj: Colors2, data: any[], valueRange: number[], size: number, overrides: boolean = false) {
    // console.log('[ColorManager]', '[getValues]', { valueRange, size, colorsObj });
    let styleValues = [];
    const scale = scaleLinear().domain([0, 100]).range(valueRange);

    if (colorsObj.type === 'continuous') {
      const { colors } = colorsObj.continuous;

      if (colorsObj.continuous.scaleType === 'exponential') {
        styleValues = this.getQuantileValues(size, data, colorsObj.fieldName, valueRange);
      }

      if (colorsObj.continuous.scaleType === 'linear') {
        for (let i = 0; i < size; i++) {
          styleValues.push(scale(i * (100 / (size - 1))));
        }
      }

      if (colorsObj.continuous.scaleType === 'quantiles') {
        const count = Math.min(...[colors.length, data.length]);
        const quantilesDomain: any = this.getQuantilesDomain(data, colorsObj.fieldName, valueRange);
        const colorScale = scaleQuantile().domain([0, 100]).range(quantilesDomain);

        for (let i = 0; i <= count; i++) {
          styleValues.push(colorScale(i * (100 / count)));
        }
      }
    }

    if (colorsObj.type === 'gradient') {
      for (let i = 0; i < size; i++) {
        styleValues.push(scale(i * (100 / (size - 1))));
      }
    }

    if (colorsObj.type === 'categorical') {
      const { values } = colorsObj.categorical;
      styleValues = values.map((value) => value.value);
    }

    if (colorsObj.type === 'range') {
      const { thresholds } = colorsObj.range;
      thresholds.forEach((threshold) => styleValues.push(threshold));
      if (overrides) {
        styleValues.splice(0, 1);
      }
    }

    return styleValues;
  }

  public getColorScale = (colorObj: Colors2, data, valueRange, extent: boolean = true) => {
    // console.log('[ColorManager]', '[getColorScale]', colorObj, data, valueRange, extent);

    if (colorObj.type === 'categorical') return this.getCategoricalScale(colorObj, data);
    if (colorObj.type === 'gradient') return this.getGradientScale(colorObj, valueRange);
    if (colorObj.type === 'range') return this.getRangeScale(colorObj);
    if (colorObj.type === 'solid') return (obj) => colorObj.solid || '#ccc';
    return this.getContinuousScale(colorObj, data, valueRange, extent);
  };

  public reevaluateColor(colorObj: Colors2, data: any) {
    const { categorical, reevaluateOnChange, activeColors } = colorObj;
    let { fallback, values, fieldName } = categorical;

    // Check if this should be reevaluated
    if (categorical && reevaluateOnChange) {
      // console.log('[ColorManager]', '[reevaluateColor]', { categorical, reevaluateOnChange, activeColors });
      const fieldValues = unique(data.map((obj) => obj[fieldName])) as any;

      // Check all values which are unmapped
      const unmappedValues = [];
      for (const fieldValue of fieldValues) {
        if (!values.map((catValue) => catValue.value).includes(fieldValue)) unmappedValues.push(fieldValue);
      }

      // Use this counter to assign the next possible color
      let cnt = 0;
      // Check if there are any removed values and replace them with the next available unmapped value
      for (const [index, value] of values.entries()) {
        if (!fieldValues.includes(value.value)) {
          if (unmappedValues.length > 0) {
            // Try to add the next available unmapped value
            values[index]['value'] = unmappedValues.shift();
          }
        }
        cnt++;
      }

      // Filter out removed values
      values = values.filter((value) => fieldValues.includes(value.value));

      const unusedColors = activeColors.filter((color) => !values.map((catValue) => catValue.color).includes(color));

      // Add any unmapped values to the end of the values array
      for (const value of unmappedValues) {
        let color;
        if (unusedColors.length > 0) {
          color = unusedColors.shift();
        } else {
          color = activeColors[cnt++ % activeColors.length];
        }
        values.push({ value, color: color || fallback });
      }

      categorical.values = values;
      // console.log({ values, fieldValues });
    }
  }

  private getCategoricalScale = (colorObj: Colors2, data: any) => {
    const { categorical } = colorObj;
    const { fallback, values, fieldName } = categorical;

    // this.reevaluateColor(colorObj, data);

    const scaleFn = (obj) => {
      const fieldValue = obj[fieldName];
      const foundValue = values.find((obj) => obj.value === fieldValue);
      return foundValue ? foundValue.color : fallback;
    };

    return scaleFn;
  };

  private getGradientScale = (colorObj: Colors2, valueRange) => {
    const { fieldName, gradient } = colorObj;
    const { values } = gradient;
    const stops = sortBy(values, ['domain']);

    const colors = stops.map((obj) => chroma(obj.color).hex('rgba'));
    // const domain = unique([0, 100, ...values.map(obj => obj.domain)]);

    // REMOVED 0, 100 for working gradient? @gregor plz tell me whats up here
    const domain: any = unique([...stops.map((obj) => obj.domain)]);

    // const colorScale = chroma
    //   .scale(colors)
    //   .domain(domain)
    //   .mode('lch');

    const colorScale = scaleLinear<string>().domain(domain).range(colors).clamp(true);

    const scaleFn = (obj) => {
      const noRange = valueRange[0] === valueRange[1]; // normalize will x/0 => NaN/Infinity
      const fieldValue = noRange ? obj[fieldName] : normalize(obj[fieldName], valueRange[0], valueRange[1]) * 100;
      return colorScale(fieldValue);
    };

    return scaleFn;
  };

  private getRangeScale = (colorObj: Colors2) => {
    const { range, fieldName } = colorObj;
    const colors: any[] = range.colors.map((color) => chroma(color).hex('rgba'));
    const fallback = chroma(range.fallback).hex('rgba');

    const thresholds = [...range.thresholds];
    thresholds[thresholds.length - 1] = thresholds[thresholds.length - 1] += thresholds[thresholds.length - 1] * 0.00001;

    const colorScale = scaleThreshold()
      .domain(thresholds)
      .range([fallback, ...colors, fallback]);

    const scaleFn = (obj) => {
      const fieldValue = obj[fieldName];
      const color = colorScale(fieldValue);
      return color;
    };

    return scaleFn;
  };

  // TODO provide data as single-value array (not object of values with fieldName access)
  private getContinuousScale = (colorObj: Colors2, data, valueRange, extent: boolean = true) => {
    const { fieldName, continuous } = colorObj;
    let { scaleType, colorSets } = continuous;
    colorSets = colorSets ?? [];

    const colors: any[] = continuous.colors.map((color) => chroma(color).hex('rgba'));

    const numColors = colors.length;
    const rangeScale = scaleLinear()
      .domain([0, numColors - 1])
      .range(valueRange);

    const domain = [];
    for (let idx = 0; idx < numColors; idx++) {
      domain.push(rangeScale(idx));
    }

    const colorSetDomains = [];
    for (const colorSet of colorSets) {
      colorSetDomains.push(colorSet.colors.map((color, idx) => rangeScale(idx)));
    }

    let colorScale;
    let colorSetScales = [];
    if (scaleType === 'linear') {
      colorScale = chroma.scale(colors).domain(domain).mode('lrgb');
      colorSetScales = colorSets.map((colorSet, idx) => chroma.scale(colorSet.colors).domain(colorSetDomains[idx]).mode('lrgb'));

      // colorScale = scaleLinear()
      //   .domain(range)
      //   .range(colors)
      //   .clamp(true);
    }

    if (scaleType === 'exponential') {
      // colorScale = scalePow() // scaleLog scalePow
      //   .domain(domain)
      //   .range(colors)
      //   .exponent(1.5)
      //   // .base(10)
      //   .clamp(true);
      const quantiles = this.getQuantileValues(colors.length, data, fieldName, valueRange, extent);
      colorScale = chroma.scale(colors).domain(quantiles).mode('lrgb');

      colorSetScales = colorSets.map((colorSet, idx) => {
        const quantiles = this.getQuantileValues(colorSet.colors.length, data, fieldName, valueRange, extent);
        return chroma.scale(colorSet.colors).domain(quantiles).mode('lrgb');
      });
    }

    if (scaleType === 'quantiles') {
      const quantilesDomain: any = unique(data.map((obj) => obj[fieldName])).sort(ascending);
      colorScale = scaleQuantile().domain(quantilesDomain).range(colors);
      colorSetScales = colorSets.map((colorSet, idx) => scaleQuantile().domain(quantilesDomain).range(colors));
    }

    const scaleFn = (obj) => {
      const fieldValue = obj[fieldName];
      if (fieldValue === null) return '#cccccc';

      const colorSet = colorSets.find((colorSet) => colorSet.enabled && colorSet.filter.every((filter) => filter.value === obj[filter.fieldName]));
      if (colorSet) {
        const color = colorSetScales[colorSets.indexOf(colorSet)](fieldValue);
        return color && color.css ? color.css() : color ? color : '#ccc';
      }

      const color = colorScale(fieldValue);
      return color && color.css ? color.css() : color ? color : '#ccc';
    };

    return scaleFn;
  };

  getQuantileValues(size: number, data, fieldName, range, extent: boolean = true) {
    size = size < 2 ? 2 : size;
    const quantilesDomain: any = this.getQuantilesDomain(data, fieldName, range, extent);
    const quantiles = [];
    for (let i = 0; i < size; i++) {
      quantiles.push(quantile(quantilesDomain, i / (size - 1)));
    }
    return quantiles; // colors.map((obj, idx) => quantile(quantilesDomain, idx / (colors.length - 1)));
  }

  getQuantilesDomain(data, fieldName, range, extent: boolean = true) {
    let values = unique(
      data
        .filter((item) => item[fieldName] !== null)
        .map((obj) => obj[fieldName])
        .sort(ascending),
    );

    if (range && extent) {
      values = values.filter((obj) => obj >= range[0] && obj <= range[1]);
      if (range[0] < values[0]) values.push(range[0]);
      if (values[values.length - 1] < range[1]) values.push(range[1]);
      values = values.sort(ascending);
    }

    return values;
  }

  getColorsByCount(colors, count) {
    return chroma.scale(colors).mode('lrgb').colors(count);
  }
}
